import { useState } from 'react';
import DataTable from '../../components/DataTable';
import { formatDate } from '../../utils/formatters';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);

  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};

const eventTypeLabels = {
  SEARCH_PLACES: 'חיפוש מקומות',
  CLICK_START_NAVIGATION: 'כפתור בדרך למקום',
  FAVOURITE_SEARCH_PLACE: 'פתיחה ממועדפים',
  SUBMIT_REPORT_LOAD: 'שליחת דיווח עומס',
  ADD_FAVORITE: 'הוספה למועדפים',
  REMOVE_FAVORITE: 'הסרה ממועדפים',
  OPEN_SEARCH: 'פתיחת חיפוש',
  CLEAR_SEARCH: 'ניקוי חיפוש',
  OPEN_PLACE: 'פתיחת מקום',
  CLOSE_PLACE: 'סגירת מקום',
  OPEN_ABOUT: 'פתיחת אודות',
  CLOSE_ABOUT: 'סגירת אודות',
  CLICK_NAVIGATE: 'לחיצה על ניווט',
  CLICK_NAVIGATE_FROM_PLACE: 'ניווט ממסך מקום',
  CLICK_NAVIGATE_FROM_ACTIVE_TRACKING: 'ניווט מבדיקת עומס פעילה',
  CLICK_MOVE_TO_MY_LOCATION: 'מעבר למיקום שלי',
  CLICK_BEST_PLACE_TO_GO_NOW: 'המקום הכי כדאי עכשיו',
  CLICK_CATEGORY: 'לחיצה על קטגוריה',
  CLICK_REPORT_LOAD_IN_PLACE: 'כפתור דיווח עומס ממסך מקום',
  CLICK_REPORT_LOAD_IN_MAP: 'כפתור דיווח עומס מהמפה',
  APP_INSTALL: 'התקנת אפליקציה',
  APP_OPEN: 'פתיחת אפליקציה',
  APP_RESUME: 'חזרה לאפליקציה',
  APP_BACKGROUND: 'מעבר לרקע',
  OPEN_FAVOURITE: 'פתיחת מועדפים',
  CLOSE_FAVOURITE: 'סגירת מועדפים',
  NAVIGATION_TIMEOUT: 'סיום ניווט בגלל זמן',
  MOVING_AWAY_FROM_DESTINATION: 'התרחקות מהיעד',
  USER_ARRIVED: 'המשתמש הגיע',
};

const formatEventType = (eventType) => eventTypeLabels[eventType] || eventType || 'לא ידוע';

const userLabel = (user = {}) =>
  user.fullName || user.anonymousId || user.deviceId || 'משתמש לא ידוע';

const sourceLabels = {
  app: 'אפליקציה',
  user: 'משתמש',
  gps: 'GPS',
};

const navigationProviderLabels = {
  waze: 'Waze',
  googleMap: 'Google Maps',
  googleMaps: 'Google Maps',
};

const categoryLabels = {
  park: 'פארק',
  beach: 'חוף',
  supermarket: 'סופרמרקט',
  pharmacy: 'בית מרקחת',
  bank: 'בנק',
  post_office: 'דואר',
  electronics_store: 'חנות אלקטרוניקה',
  shopping_mall: 'קניון',
  restaurant: 'מסעדה',
  cafe: 'בית קפה',
  gym: 'חדר כושר',
  gas_station: 'תחנת דלק',
  default: 'כללי',
};

const formatSource = (source) => sourceLabels[source] || source || 'לא ידוע';
const formatNavigationProvider = (provider) =>
  navigationProviderLabels[provider] || provider || 'לא ידוע';
const formatCategory = (category) => categoryLabels[category] || category || 'לא ידוע';
const formatLoadScore = (score) => (score ? `ציון ${score}` : 'לא ידוע');

const importantEventTypes = [
  'APP_OPEN',
  'CLICK_START_NAVIGATION',
  'CLICK_NAVIGATE_FROM_ACTIVE_TRACKING',
  'CLICK_NAVIGATE_FROM_PLACE',
  'CLICK_REPORT_LOAD_IN_PLACE',
  'CLICK_REPORT_LOAD_IN_MAP',
  'SUBMIT_REPORT_LOAD',
];

const columns = [
  {
    key: 'eventType',
    label: 'סוג',
    render: (event) => <span title={event.eventType}>{formatEventType(event.eventType)}</span>,
  },
  {
    key: 'source',
    label: 'מקור',
    render: (event) => event.source || '-',
  },
  {
    key: 'user',
    label: 'משתמש',
    render: (event) => event.userId || event.anonymousId || event.deviceId || '-',
  },
  {
    key: 'place',
    label: 'מקום',
    render: (event) => event.metadata?.placeId || '-',
  },
  {
    key: 'createdAt',
    label: 'נוצר',
    render: (event) => formatDate(event.createdAt),
  },
];
const UniqueUsersDialog = ({ open, title, users = [], total = 0, onClose }) => {
  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section
        className="modal analytics-users-modal"
        onMouseDown={(event) => event.stopPropagation()}
        dir="rtl"
      >
        <header className="modal-header">
          <div>
            <h2>{title}</h2>
            <span>
              {total} משתמשים ייחודיים
              {users.length < total ? ` · מוצגים ${users.length} ראשונים` : ''}
            </span>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="modal-body analytics-users-body">
          {users.length === 0 ? (
            <p className="muted">אין משתמשים להצגה</p>
          ) : (
            <div className="analytics-users-list">
              {users.map((user) => (
                <article
                  className="analytics-user-row"
                  key={user.anonymousId || user.deviceId || `${userLabel(user)}-${user.eventsCount}`}
                >
                  <div>
                    <strong>{userLabel(user)}</strong>
                    <small>{user.platform || 'unknown'}</small>
                  </div>
                  <div>
                    <span>{user.eventsCount || 0} אירועים</span>
                    <small>אירוע אחרון: {user.lastEventAt ? formatDate(user.lastEventAt) : '-'}</small>
                  </div>
                  <div>
                    <span title={user.anonymousId || ''}>{user.anonymousId || '-'}</span>
                    <small title={user.deviceId || ''}>Device: {user.deviceId || '-'}</small>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const EventTypeCard = ({ item, total, highlighted = false, onOpenUsers }) => {
  return (
    <article className={`panel event-type-card ${highlighted ? 'important' : ''}`}>
      <span title={item._id || 'unknown'}>{formatEventType(item._id)}</span>
      <strong>{item.count || 0}</strong>
      <button
        type="button"
        className="analytics-users-link"
        onClick={() =>
          onOpenUsers({
            title: `משתמשים ייחודיים · ${formatEventType(item._id)}`,
            users: item.users || [],
            total: item.uniqueUsers || 0,
          })
        }
      >
        {item.uniqueUsers || 0} משתמשים ייחודיים
      </button>
    </article>
  );
};

const EventTypeSummary = ({ items = [], total = 0, uniqueUsers = 0, uniqueUserDetails = [] }) => {
  const [selectedUsers, setSelectedUsers] = useState(null);
  const itemsByType = new Map(items.map((item) => [item._id || 'unknown', item]));
  const importantItems = importantEventTypes.map((eventType) => (
    itemsByType.get(eventType) || {
      _id: eventType,
      count: 0,
      uniqueUsers: 0,
      users: [],
    }
  ));
  const otherItems = items.filter((item) => !importantEventTypes.includes(item._id || 'unknown'));

  const openUsers = ({ title, users, total }) => {
    setSelectedUsers({ title, users, total });
  };

  return (
    <div className="analytics-summary-view">
      <header className="analytics-summary-header">
        <div className="analytics-summary-totals">
          <strong>{total} אירועים</strong>

        </div>
      </header>

      {items.length === 0 ? (
        <article className="panel analytics-empty-summary">
          אין נתונים בטווח הזה
        </article>
      ) : (
        <div className="analytics-event-sections">
          <section className="analytics-event-section">
            <div className="event-type-grid">
              {importantItems.map((item) => (
                <EventTypeCard
                  key={item._id || 'unknown'}
                  item={item}
                  total={total}
                  highlighted
                  onOpenUsers={openUsers}
                />
              ))}
            </div>
          </section>

          {otherItems.length ? (
            <>
              <div className="analytics-section-divider">
                <span></span>
              </div>
              <section className="analytics-event-section">
                <div className="event-type-grid">
                  {otherItems.map((item) => (
                    <EventTypeCard
                      key={item._id || 'unknown'}
                      item={item}
                      total={total}
                      onOpenUsers={openUsers}
                    />
                  ))}
                </div>
              </section>
            </>
          ) : null}
        </div>
      )
      }

      <UniqueUsersDialog
        open={Boolean(selectedUsers)}
        title={selectedUsers?.title || ''}
        users={selectedUsers?.users || []}
        total={selectedUsers?.total || 0}
        onClose={() => setSelectedUsers(null)}
      />
    </div >
  );
};

const BreakdownCard = ({ title, description, items = [], total = 0, formatLabel = (value) => value }) => {
  const max = Math.max(1, ...items.map((item) => item.count || 0));

  return (
    <article className="panel analytics-breakdown-card">
      <header className="chart-header">
        <div>
          <h2>{title}</h2>
          <span>{description}</span>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="analytics-breakdown-empty">אין נתונים להצגה בטווח הזה</div>
      ) : (
        <div className="analytics-breakdown-list">
          {items.map((item) => {
            const count = item.count || 0;
            const percentOfTotal = total > 0 ? Math.round((count / total) * 100) : 0;
            const barWidth = Math.max(4, (count / max) * 100);
            const label = formatLabel(item._id);

            return (
              <div className="analytics-breakdown-row" key={`${title}-${item._id || 'unknown'}`}>
                <div className="analytics-breakdown-row-head">
                  <strong title={item._id || ''}>{label}</strong>
                  <span>
                    {count} · {percentOfTotal}%
                  </span>
                </div>
                <div className="analytics-breakdown-bar" aria-hidden="true">
                  <div style={{ width: `${barWidth}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
};

const AnalyticsBreakdowns = ({ reports = {}, total = 0 }) => {
  const breakdowns = reports.breakdowns || {};
  const daily = [...(breakdowns.daily || reports.daily || [])].reverse();

  return (
    <div className="analytics-breakdowns-view">
      <header className="analytics-summary-header">
        <div>
          <h2>פילוחים</h2>
          <span>כל הנתונים כאן מושפעים מטווח התאריכים והפילטרים של האנליטיקס</span>
        </div>
        <div className="analytics-summary-totals">
          <strong>{total} אירועים</strong>
        </div>
      </header>

      <div className="analytics-breakdown-grid">
        <BreakdownCard
          title="מקומות מובילים"
          description="לפי Place ID"
          items={breakdowns.byPlace || []}
          total={total}
          formatLabel={(placeId) => placeId || 'ללא מקום'}
        />
        <BreakdownCard
          title="ניווט לפי אפליקציה"
          description="Waze / Google Maps כשנשלח מהאפליקציה"
          items={breakdowns.byNavigationProvider || []}
          total={total}
          formatLabel={formatNavigationProvider}
        />
        <BreakdownCard
          title="קטגוריות"
          description="לחיצות קטגוריה ואירועים עם קטגוריה"
          items={breakdowns.byCategory || []}
          total={total}
          formatLabel={formatCategory}
        />
        <BreakdownCard
          title="דיווחי עומס לפי ציון"
          description="רק אירועים ששמרו loadScore"
          items={breakdowns.byLoadScore || []}
          total={total}
          formatLabel={formatLoadScore}
        />
      </div>
    </div>
  );
};

const AnalyticsPage = ({ analytics, filters, setFilters, loadAnalytics, loading }) => {
  const [activeView, setActiveView] = useState('table');
  const defaultDateRange = getDefaultDateRange();
  const fromValue = filters.from || defaultDateRange.from;
  const toValue = filters.to || defaultDateRange.to;

  return (
    <section className="work-area table-page analytics-page" dir="rtl">
      <header className="section-header">
        <div>
          <h1>אנליטיקס</h1>
          <span>{analytics.total} אירועים</span>
        </div>
        <div className="filters">
          <input
            type="date"
            value={fromValue}
            onChange={(e) =>
              setFilters({
                ...filters,
                from: e.target.value,
              })
            }
          />
          <input
            type="date"
            value={toValue}
            onChange={(e) =>
              setFilters({
                ...filters,
                to: e.target.value,
              })
            }
          />
          <input
            placeholder="Place ID"
            value={filters.placeId}
            onChange={(e) =>
              setFilters({
                ...filters,
                placeId: e.target.value,
              })
            }
          />
          <input
            placeholder="Source"
            value={filters.source}
            onChange={(e) =>
              setFilters({
                ...filters,
                source: e.target.value,
              })
            }
          />
          <input
            placeholder="Event type"
            value={filters.eventType}
            onChange={(e) =>
              setFilters({
                ...filters,
                eventType: e.target.value,
              })
            }
          />
          <button onClick={() => loadAnalytics(1)}>סינון</button>
        </div>
      </header>

      <div className="inner-tabs">
        <button
          type="button"
          className={activeView === 'table' ? 'active' : ''}
          onClick={() => setActiveView('table')}
        >
          טבלת אירועים
        </button>
        <button
          type="button"
          className={activeView === 'summary' ? 'active' : ''}
          onClick={() => setActiveView('summary')}
        >
          סיכומים
        </button>
        <button
          type="button"
          className={activeView === 'breakdowns' ? 'active' : ''}
          onClick={() => setActiveView('breakdowns')}
        >
          פילוחים
        </button>
      </div>

      {activeView === 'breakdowns' ? (
        <AnalyticsBreakdowns
          reports={analytics.reports || {}}
          total={analytics.total || 0}
        />
      ) : activeView === 'summary' ? (
        <EventTypeSummary
          items={analytics.reports?.byEventType || []}
          total={analytics.total || 0}
          uniqueUsers={analytics.reports?.uniqueUsers || 0}
          uniqueUserDetails={analytics.reports?.uniqueUserDetails || []}
        />
      ) : (
        <DataTable
          columns={columns}
          items={analytics.items}
          rowKey={(event) => event._id || event.id}
          page={analytics.page}
          limit={analytics.limit}
          total={analytics.total}
          onPageChange={loadAnalytics}
          loading={loading}
        />
      )}
    </section>
  );
};
export default AnalyticsPage;
